
import { Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import Notification from '@/mixins/Notification';
import { Page } from '@/interfaces/api/Page';
import { namespace } from 'vuex-class';
import VWrapper from '@/components/shared/VWrapper.vue';
import { SyncConfig } from '@/interfaces/models/SyncConfig';
import { Venue } from '@/interfaces/models/Venue';
import SyncJobTable from '@/components/aggregatorSyncs/SyncJobTable.vue';
import Filter from '@/interfaces/api/Filter';
import { AggregatorSyncJob } from '@/interfaces/models/AggregatorSyncJob';
import { Permission } from '@/enums/Permission';
import { SyncKey } from '@/enums/SyncKey';

const venue = namespace('venue');
const aggregatorSyncs = namespace('aggregatorSyncs');

@Component({
  components: { VWrapper, SyncJobTable },
})
export default class AggregatorSyncs extends mixins(Notification) {
  @venue.State('active') public venue!: Venue;
  @aggregatorSyncs.Action('fetchWolt') public fetchWolt!: any;
  @aggregatorSyncs.Action('fetchJet') public fetchJet!: any;
  @aggregatorSyncs.Action('fetchUberEats') public fetchUberEats!: any;
  @aggregatorSyncs.Action('fetchDeliveroo') public fetchDeliveroo!: any;
  @aggregatorSyncs.Action('setFilter') public setFilter!: any;
  @aggregatorSyncs.State((state) => state.wolt.items) public wolt!: AggregatorSyncJob[];
  @aggregatorSyncs.State((state) => state.jet.items) public jet!: AggregatorSyncJob[];
  @aggregatorSyncs.State((state) => state.uberEats.items) public uberEats!: AggregatorSyncJob[];
  @aggregatorSyncs.State((state) => state.deliveroo.items) public deliveroo!: AggregatorSyncJob[];
  @aggregatorSyncs.State((state) => state.wolt.pagination.total) public totalWolt!: number;
  @aggregatorSyncs.State((state) => state.jet.pagination.total) public totalJet!: number;
  @aggregatorSyncs.State((state) => state.uberEats.pagination.total) public totalUberEats!: number;
  @aggregatorSyncs.State((state) => state.deliveroo.pagination.total) public totalDeliveroo!: number;
  @aggregatorSyncs.State((state) => state.wolt.filter) public filterWolt!: Filter;
  @aggregatorSyncs.State((state) => state.jet.filter) public filterJet!: Filter;
  @aggregatorSyncs.State((state) => state.uberEats.filter) public filterUberEats!: Filter;
  @aggregatorSyncs.State((state) => state.deliveroo.filter) public filterDeliveroo!: Filter;

  public configDialog: Boolean = false;
  public config: SyncConfig | null = null;

  public async created() {
    if (!this.venue) {
      this.$startLoading('venue');
    }

    if (this.venue?._id) {
      await this.fetch();
    }
  }

  public async fetch() {
    const filter = { venue: this.venue._id, page: 1, limit: 10 };

    if (this.$can(Permission.WOLT_SYNC_JOB_VIEW)) {
      this.setFilter({ filter, key: SyncKey.WOLT });
      await this.fetchWolt();
    }

    if (this.$can(Permission.JET_SYNC_JOB_VIEW)) {
      this.setFilter({ filter, key: SyncKey.JET });
      await this.fetchJet();
    }

    if (this.$can(Permission.UBER_EATS_SYNC_JOB_VIEW)) {
      this.setFilter({ filter, key: SyncKey.UBER_EATS });
      await this.fetchUberEats();
    }

    if (this.$can(Permission.DELIVEROO_SYNC_JOB_VIEW)) {
      this.setFilter({ filter, key: SyncKey.DELIVEROO });
      await this.fetchDeliveroo();
    }

    this.$stopAllLoading();
  }

  public async onPage(page: Page, provider: string) {
    if (page) {
      if (provider === SyncKey.WOLT) {
        this.setFilter({ filter: { ...this.filterWolt, ...page }, key: SyncKey.WOLT });
        await this.fetchWolt();
      }

      if (provider === SyncKey.JET) {
        this.setFilter({ filter: { ...this.filterJet, ...page }, key: SyncKey.JET });
        await this.fetchJet();
      }

      if (provider === SyncKey.UBER_EATS) {
        this.setFilter({ filter: { ...this.filterUberEats, ...page }, key: SyncKey.UBER_EATS });
        await this.fetchUberEats();
      }

      if (provider === SyncKey.DELIVEROO) {
        this.setFilter({ filter: { ...this.filterDeliveroo, ...page }, key: SyncKey.DELIVEROO });
        await this.fetchDeliveroo();
      }
    }
  }

  public async onSearch(queries: any, provider: string) {
    this.setFilter({ filter: { page: 1, limit: 10, venue: this.venue?._id, ...queries }, key: provider });

    if (provider === SyncKey.WOLT) {
      await this.fetchWolt();
    }

    if (provider === SyncKey.JET) {
      await this.fetchJet();
    }

    if (provider === SyncKey.UBER_EATS) {
      await this.fetchUberEats();
    }

    if (provider === SyncKey.DELIVEROO) {
      await this.fetchDeliveroo();
    }
  }

  @Watch('venue')
  public async onVenueChange() {
    if (this.venue && this.venue._id) {
      await this.fetch();
    }
  }
}
